import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';

export class ClientCacheRefreshEvent extends NotificationEvent {
  static readonly TYPE_ID: string = '493fe0ff4785467995b16a3103b1737f';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly seen: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return ClientCacheRefreshEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ClientCacheRefreshEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ClientCacheRefreshEvent {
    return new ClientCacheRefreshEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new ClientCacheRefreshEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.seen === this.seen;
  }

  markAsSeen(): this {
    return new ClientCacheRefreshEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
    ) as this;
  }
}

export class ClientCacheRefreshEventBuilder extends DataModelBuilder<ClientCacheRefreshEvent> {
  build(context: SerializationContext, map: SerializedData): ClientCacheRefreshEvent {
    return ClientCacheRefreshEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ClientCacheRefreshEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ClientCacheRefreshEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
